import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Package, CheckCircle2, ArrowRight, Users, CreditCard, Smartphone, Zap } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { REQ } from "@/constants/testIds/cardRequests";

/**
 * Dialog di richiesta nuove card RecenUp.
 * Props: open, onOpenChange, businesses[], user, defaultBusinessId, onCreated(request)
 */
export default function RequestCardDialog({ open, onOpenChange, businesses = [], user, defaultBusinessId, onCreated }) {
  const [businessId, setBusinessId] = useState(defaultBusinessId || "");
  const [qty, setQty] = useState(1);
  const [isCustom, setIsCustom] = useState(false);
  const [customQty, setCustomQty] = useState(10);
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);

  useEffect(() => {
    if (!open) return;
    setBusinessId(defaultBusinessId || businesses[0]?.business_id || "");
    setQty(1);
    setIsCustom(false);
    setCustomQty(10);
    setContactName(user ? [user.first_name, user.last_name].filter(Boolean).join(" ") || user.name || "" : "");
    setEmail(user?.email || "");
    setPhone("");
    setShippingAddress("");
    setNotes("");
    setSuccess(null);
    (async () => {
      try {
        const { data } = await api.get("/card-pricing/public");
        setPricing(data);
      } catch {
        setPricing(null);
      }
      try {
        const { data } = await api.get("/credit/balance");
        setCreditBalance(Number(data?.balance_eur || 0));
      } catch {
        setCreditBalance(0);
      }
    })();
  }, [open, defaultBusinessId, user, businesses]);

  const priceInfo = useMemo(() => {
    if (!pricing) return null;
    if (isCustom) {
      return { label: "Prezzo personalizzato", note: "Ti contatteremo per un preventivo su misura.", total: null };
    }
    const key = String(qty);
    const bundle = pricing.bundle_prices?.[key];
    if (bundle === undefined) return null;
    const minFree = Number(pricing.free_shipping_from_qty) || 3;
    const freeShipping = qty >= minFree;
    const shipping = freeShipping ? 0 : (Number(pricing.shipping_cost) || 0);
    const total = Number(bundle) + shipping;
    // Preview credit application (mirrors backend _reserve_credit_for_order)
    let creditApplied = 0;
    if (creditBalance > 0) {
      if (creditBalance >= total) creditApplied = total;
      else creditApplied = Math.min(creditBalance, Math.max(0, total - 0.5));
    }
    const remaining = Math.max(0, Number((total - creditApplied).toFixed(2)));
    return {
      subtotal: Number(bundle),
      shipping,
      freeShipping,
      total,
      creditApplied: Number(creditApplied.toFixed(2)),
      remaining,
      minFree,
    };
  }, [pricing, qty, isCustom, creditBalance]);

  const euro = (n) => (n === null || n === undefined ? "—" : `€ ${Number(n).toFixed(2).replace(".", ",")}`);

  useEffect(() => {
    const biz = businesses.find((b) => b.business_id === businessId);
    if (biz && !shippingAddress) setShippingAddress(biz.address || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!businessId) return toast.error("Seleziona un'attività");
    if (!contactName.trim() || !email.trim() || !phone.trim() || !shippingAddress.trim())
      return toast.error("Compila tutti i campi obbligatori");
    const finalQty = isCustom ? Number(customQty) : qty;
    if (isCustom && (!finalQty || finalQty <= 5))
      return toast.error("Per una richiesta personalizzata inserisci una quantità superiore a 5");

    setSubmitting(true);
    try {
      const { data } = await api.post("/card-requests", {
        business_id: businessId,
        quantity: finalQty,
        is_custom: isCustom,
        contact_name: contactName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        shipping_address: shippingAddress.trim(),
        notes: notes.trim() || null,
      });
      setSuccess(data);
      onCreated?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante l'invio");
    } finally {
      setSubmitting(false);
    }
  };

  const payNow = async () => {
    if (!businessId) return toast.error("Seleziona un'attività");
    if (isCustom) return toast.error("Il pagamento immediato è disponibile solo per 1–5 card");
    if (!contactName.trim() || !email.trim() || !phone.trim() || !shippingAddress.trim())
      return toast.error("Compila tutti i campi obbligatori per pagare");
    setSubmitting(true);
    try {
      const { data } = await api.post("/card-requests/instant-checkout", {
        business_id: businessId,
        quantity: qty,
        is_custom: false,
        contact_name: contactName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        shipping_address: shippingAddress.trim(),
        notes: notes.trim() || null,
      });
      // Redirect to Stripe hosted checkout (Google Pay / Apple Pay / Card)
      window.location.href = data.payment_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante l'avvio del pagamento");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-1)] max-w-lg max-h-[90vh] overflow-y-auto">
        {success ? (
          <div
            data-testid={REQ.form.successMessage}
            className="text-center py-6"
          >
            <div className="mx-auto h-14 w-14 rounded-full bg-[#4A8A6E]/12 flex items-center justify-center text-[#3E7A5E] mb-5">
              <CheckCircle2 size={28} strokeWidth={1.6} />
            </div>
            <h2 className="font-serif text-3xl text-[var(--fg-1)] tracking-tight">
              Richiesta <span className="italic">ricevuta!</span>
            </h2>
            <p className="mt-3 text-sm text-[var(--fg-3)] leading-relaxed max-w-sm mx-auto">
              Abbiamo ricevuto la tua richiesta di RecenUp Card. La controlleremo e ti contatteremo per confermare i dettagli.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--bg-3)] border border-[var(--border-c)] px-4 py-2">
              <Package size={14} className="text-[var(--fg-3)]" />
              <span className="font-mono text-xs text-[var(--fg-1)]">{success.request_id}</span>
              <span className="text-[var(--fg-4)] text-xs">·</span>
              <span className="text-xs text-[var(--fg-2)]">
                {success.quantity} {success.quantity === 1 ? "card" : "card"}
                {success.is_custom && " (personalizzata)"}
              </span>
            </div>
            <div className="mt-6">
              <button
                onClick={() => onOpenChange(false)}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] px-5 py-2.5 text-sm font-sans"
                style={{ transition: "background-color 0.2s ease" }}
              >
                Chiudi
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[var(--fg-1)]">
                Richiedi nuove card
              </DialogTitle>
            </DialogHeader>

            {businesses.length > 1 && (
              <div>
                <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Attività
                </Label>
                <Select value={businessId} onValueChange={setBusinessId}>
                  <SelectTrigger data-testid={REQ.form.businessSelect} className="mt-2">
                    <SelectValue placeholder="Seleziona un'attività" />
                  </SelectTrigger>
                  <SelectContent>
                    {businesses.map((b) => (
                      <SelectItem key={b.business_id} value={b.business_id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                Quantità di card
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={REQ.form[`qty${n}`]}
                    onClick={() => {
                      setIsCustom(false);
                      setQty(n);
                    }}
                    className={`h-11 min-w-[52px] px-3 rounded-full text-sm font-sans font-semibold ${
                      !isCustom && qty === n
                        ? "bg-[var(--fg-1)] text-[var(--bg-1)]"
                        : "bg-[var(--bg-3)] border border-[var(--border-c)] text-[var(--fg-1)] hover:border-[var(--fg-2)]"
                    }`}
                    style={{ transition: "background-color 0.15s ease, border-color 0.15s ease" }}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid={REQ.form.qtyMore}
                  onClick={() => setIsCustom(true)}
                  className={`h-11 px-4 rounded-full text-sm font-sans ${
                    isCustom
                      ? "bg-[var(--fg-1)] text-[var(--bg-1)] font-semibold"
                      : "bg-[var(--bg-3)] border border-[var(--border-c)] text-[var(--fg-1)] hover:border-[var(--fg-2)]"
                  }`}
                  style={{ transition: "background-color 0.15s ease, border-color 0.15s ease" }}
                >
                  <Users size={13} className="inline mr-1.5 -mt-0.5" />
                  Ho bisogno di più di 5 card
                </button>
              </div>

              <AnimatePresence initial={false}>
                {isCustom && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4">
                      <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                        Quante card ti servono?
                      </Label>
                      <Input
                        data-testid={REQ.form.qtyCustomInput}
                        type="number"
                        min={6}
                        max={500}
                        value={customQty}
                        onChange={(e) => setCustomQty(e.target.value)}
                        className="mt-2 max-w-[140px]"
                      />
                      <p className="mt-2 text-xs text-[var(--fg-3)] leading-relaxed">
                        Per richieste superiori a 5 card, ti contatteremo personalmente per verificare le esigenze della tua attività.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Live pricing + payment methods */}
              {priceInfo && (
                <div
                  data-testid="request-price-preview"
                  className="mt-4 rounded-xl border border-[var(--border-c)] bg-[var(--bg-3)]/40 p-4"
                >
                  {priceInfo.total !== null ? (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">
                            {priceInfo.creditApplied > 0 ? "Totale da pagare (dopo credito)" : "Totale da pagare"}
                          </div>
                          <div className="mt-1 font-serif text-3xl text-[var(--fg-1)] tracking-tight" data-testid="request-price-total">
                            {euro(priceInfo.remaining)}
                          </div>
                          {priceInfo.creditApplied > 0 && (
                            <div className="mt-1 text-[11px] text-[#3E7A5E] font-sans" data-testid="request-price-credit-applied">
                              -{euro(priceInfo.creditApplied)} credito referral applicato
                            </div>
                          )}
                        </div>
                        <div className="text-right text-xs text-[var(--fg-3)] font-mono leading-relaxed">
                          <div>{qty} card: {euro(priceInfo.subtotal)}</div>
                          <div>
                            Spedizione: {priceInfo.freeShipping ? (
                              <span className="text-[#3E7A5E] font-sans font-semibold not-italic">GRATIS</span>
                            ) : (
                              euro(priceInfo.shipping)
                            )}
                          </div>
                          {priceInfo.creditApplied > 0 && (
                            <div className="text-[#3E7A5E]">Subtotale: {euro(priceInfo.total)}</div>
                          )}
                        </div>
                      </div>
                      {!priceInfo.freeShipping && (
                        <div className="mt-2 text-[11px] text-[#00D6D8] font-sans">
                          🎁 Aggiungi {priceInfo.minFree - qty} {priceInfo.minFree - qty === 1 ? "card" : "card"} per avere la spedizione <strong>gratuita</strong>.
                        </div>
                      )}
                      <div className="mt-4 pt-4 border-t border-[var(--border-c)]">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans mb-2">
                          Metodi di pagamento accettati
                        </div>
                        <div className="flex flex-wrap items-center gap-2" data-testid="request-payment-methods">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-1)] border border-[var(--border-c)] px-3 py-1.5 text-xs text-[var(--fg-1)] font-sans">
                            <CreditCard size={12} strokeWidth={1.8} /> Carta
                          </span>
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-1)] border border-[var(--border-c)] px-3 py-1.5 text-xs text-[var(--fg-1)] font-sans">
                            <Smartphone size={12} strokeWidth={1.8} /> Apple Pay
                          </span>
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-1)] border border-[var(--border-c)] px-3 py-1.5 text-xs text-[var(--fg-1)] font-sans">
                            <Smartphone size={12} strokeWidth={1.8} /> Google Pay
                          </span>
                        </div>
                        <p className="mt-2 text-[11px] text-[var(--fg-3)] leading-relaxed">
                          Riceverai il link di pagamento via email dopo l'approvazione della richiesta.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans mb-1">
                        {priceInfo.label}
                      </div>
                      <p className="text-xs text-[var(--fg-2)]">{priceInfo.note}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Nome referente
                </Label>
                <Input
                  data-testid={REQ.form.contactName}
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Marco Rossi"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Email
                </Label>
                <Input
                  data-testid={REQ.form.email}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@esempio.it"
                  className="mt-2"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Numero di telefono
                </Label>
                <Input
                  data-testid={REQ.form.phone}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+39 ..."
                  className="mt-2"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Indirizzo di spedizione
                </Label>
                <Textarea
                  data-testid={REQ.form.shippingAddress}
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder="Via ..., Città, CAP"
                  className="mt-2 min-h-[64px]"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Note (opzionali)
                </Label>
                <Textarea
                  data-testid={REQ.form.notes}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Es. richieste particolari o data di consegna desiderata"
                  className="mt-2 min-h-[64px]"
                />
              </div>
            </div>

            <DialogFooter className="flex-col gap-2">
              {!isCustom && priceInfo && priceInfo.total !== null && (
                <button
                  type="button"
                  data-testid="request-pay-now-btn"
                  onClick={payNow}
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] px-5 py-3 text-sm font-sans font-semibold disabled:opacity-60"
                  style={{ transition: "background-color 0.2s ease" }}
                >
                  <Zap size={15} strokeWidth={2.2} />
                  {submitting
                    ? "Avvio pagamento…"
                    : priceInfo.remaining <= 0
                      ? `Completa con credito · ${euro(priceInfo.creditApplied)}`
                      : `Paga ora ${euro(priceInfo.remaining)} · Google Pay / Apple Pay / Carta`}
                </button>
              )}
              <div className="w-full flex flex-col-reverse md:flex-row md:justify-end gap-2">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-5 py-2.5 text-sm font-sans"
                  style={{ transition: "border-color 0.2s ease" }}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  data-testid={REQ.form.submitBtn}
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold disabled:opacity-60"
                  style={{ transition: "background-color 0.2s ease" }}
                >
                  <Send size={14} />
                  {submitting ? "Invio..." : (isCustom ? "Richiedi preventivo" : "Salva senza pagare")}
                  {!submitting && <ArrowRight size={14} />}
                </button>
              </div>
              {!isCustom && (
                <p className="text-[11px] text-[var(--fg-3)] text-center leading-relaxed w-full">
                  Preferisci pagare dopo? Salva la richiesta e riceverai il link via email.
                </p>
              )}
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
